// JSON-LD structured-data builders, gated by VITE_FF_JSONLD_SCHEMA at the call
// sites (src/lib/public-seo.ts for landings, src/app-ui.tsx for blog articles).
//
// This module is intentionally self-contained: pure object builders plus
// display-price parsing. It performs NO DOM work and reads NO flag — the
// callers own injection + flag gating so OFF stays bit-identical with main.
//
// It imports only from ./pricing and ./currency (neither imports this module),
// so there is no import cycle with public-seo.ts.

import { PLAN_PRICES } from './pricing'
import { currencyForLanguage } from './currency'

const ORIGIN = 'https://www.bookbuildergo.com'

// The 10 public URL locales (mirrors SEO_LOCALES in public-seo.ts; kept local to
// avoid an import cycle, since public-seo.ts imports the builders from here).
export type JsonLdLocale =
  | 'da' | 'en' | 'de' | 'es' | 'fr' | 'it' | 'nl' | 'pl' | 'pt' | 'se'

// BCP-47 language tag per URL locale. Matches the URL code except Swedish
// (/se advertises "sv"), consistent with htmlLangByLocale in public-seo.ts.
const LANG_TAG: Record<JsonLdLocale, string> = {
  da: 'da', en: 'en', de: 'de', es: 'es', fr: 'fr',
  it: 'it', nl: 'nl', pl: 'pl', pt: 'pt', se: 'sv',
}

// Localized breadcrumb labels. "Blog" is the visible footer/heading term in every
// locale except Swedish ("Blogg", matching the /se blog heading "BookBuilderGo-bloggen").
const BREADCRUMB_LABELS: Record<JsonLdLocale, { home: string; blog: string }> = {
  da: { home: 'Hjem', blog: 'Blog' },
  en: { home: 'Home', blog: 'Blog' },
  de: { home: 'Startseite', blog: 'Blog' },
  es: { home: 'Inicio', blog: 'Blog' },
  fr: { home: 'Accueil', blog: 'Blog' },
  it: { home: 'Home', blog: 'Blog' },
  nl: { home: 'Home', blog: 'Blog' },
  pl: { home: 'Strona główna', blog: 'Blog' },
  pt: { home: 'Início', blog: 'Blog' },
  se: { home: 'Hem', blog: 'Blogg' },
}

export function langTagForLocale(locale: JsonLdLocale): string {
  return LANG_TAG[locale] || 'en'
}

/**
 * Parse the integer amount out of a display price string ("$11" → 11,
 * "79 kr" → 79, "€59" → 59, "45 zł" → 45). Monthly display values carry no
 * thousands separators, so stripping non-digits is exact for the low/high pair.
 */
export function parseDisplayPrice(display: string): number {
  const digits = String(display ?? '').replace(/[^\d]/g, '')
  return digits ? parseInt(digits, 10) : 0
}

const ORG_NODE = {
  '@type': 'Organization',
  '@id': `${ORIGIN}/#org`,
  name: 'BookBuilderGo',
  url: `${ORIGIN}/`,
  // /pwa/icon-512.png is a real static PNG (verified image/png); og-image.jpg
  // resolves to the SPA shell (text/html), so it is deliberately NOT used here.
  logo: `${ORIGIN}/pwa/icon-512.png`,
}

/** Landing @graph: Organization + WebSite + SoftwareApplication (+ AggregateOffer). */
export function buildLandingGraph(locale: JsonLdLocale) {
  const lang = langTagForLocale(locale)
  const currency = currencyForLanguage(locale)
  const lowPrice = parseDisplayPrice(PLAN_PRICES.starter[currency].monthly)
  const highPrice = parseDisplayPrice(PLAN_PRICES.pro[currency].monthly)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      ORG_NODE,
      {
        '@type': 'WebSite',
        '@id': `${ORIGIN}/#site`,
        name: 'BookBuilderGo',
        url: `${ORIGIN}/`,
        publisher: { '@id': `${ORIGIN}/#org` },
        inLanguage: lang,
      },
      {
        '@type': 'SoftwareApplication',
        name: 'BookBuilderGo',
        url: `${ORIGIN}/${locale}`,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        publisher: { '@id': `${ORIGIN}/#org` },
        inLanguage: lang,
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: currency,
          lowPrice,
          highPrice,
          offerCount: 3,
        },
      },
    ],
  }
}

export interface BlogGraphInput {
  locale: JsonLdLocale
  /** Canonical article URL (same value the canonical <link> uses). */
  url: string
  title: string
  description: string
  /** Real publish date, e.g. "2026-05-17". Also used for dateModified (no separate field exists). */
  datePublished: string
  /** Author display name, e.g. "BookBuilderGo Team". */
  authorName: string
}

/** Blog @graph: BlogPosting + BreadcrumbList (Home → Blog → article title). */
export function buildBlogGraph(input: BlogGraphInput) {
  const lang = langTagForLocale(input.locale)
  const labels = BREADCRUMB_LABELS[input.locale] || BREADCRUMB_LABELS.en
  const homeUrl = `${ORIGIN}/${input.locale}`
  const blogUrl = `${ORIGIN}/${input.locale}/blog`
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: input.title,
        description: input.description,
        inLanguage: lang,
        datePublished: input.datePublished,
        // No separate modified date exists in BLOG_POST_META — reuse datePublished
        // rather than inventing a newer date.
        dateModified: input.datePublished,
        author: { '@type': 'Organization', name: input.authorName },
        publisher: {
          '@type': 'Organization',
          name: 'BookBuilderGo',
          logo: { '@type': 'ImageObject', url: `${ORIGIN}/pwa/icon-512.png` },
        },
        mainEntityOfPage: input.url,
        // image deliberately omitted: og-blog-placeholder.png does not exist as a
        // real image (resolves to the SPA shell). We do not invent a cover.
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: labels.home, item: homeUrl },
          { '@type': 'ListItem', position: 2, name: labels.blog, item: blogUrl },
          { '@type': 'ListItem', position: 3, name: input.title },
        ],
      },
    ],
  }
}
